import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Role, Capability } from '../../types/auth';
import { useCreateRole, useUpdateRole, useDeleteRole, useCapabilities } from '../../api/admin';
import { useQueryClient } from '@tanstack/react-query';
import AdminPageHeader from './AdminPageHeader';

interface RolesManagementProps {
    roles: Role[];
}

const errorMessage = (err: unknown, fallback: string) =>
    (err as { message?: string })?.message || fallback;

export default function RolesManagement({ roles: initialRoles }: RolesManagementProps) {
    const [open, setOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        capability_ids: [] as number[],
    });
    const [formError, setFormError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

    const queryClient = useQueryClient();
    const createRole = useCreateRole();
    const updateRole = useUpdateRole(editingRole?.id?.toString() || '');
    const deleteRole = useDeleteRole();
    const { data: capabilities = [], isLoading: isLoadingCapabilities } = useCapabilities();

    const handleOpen = (role?: Role) => {
        setFormError(null);
        if (role) {
            setEditingRole(role);
            setFormData({
                name: role.name,
                capability_ids: role.capabilities.map((c: Capability) => c.id),
            });
        } else {
            setEditingRole(null);
            setFormData({
                name: '',
                capability_ids: [],
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingRole(null);
        setFormError(null);
        setFormData({
            name: '',
            capability_ids: [],
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        try {
            if (editingRole) {
                await updateRole.mutateAsync({
                    name: formData.name,
                    capability_ids: formData.capability_ids,
                });
            } else {
                await createRole.mutateAsync({
                    name: formData.name,
                    capability_ids: formData.capability_ids,
                });
            }
            handleClose();
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            setSnackbar({ message: editingRole ? 'Role updated.' : 'Role created.', severity: 'success' });
        } catch (error) {
            setFormError(errorMessage(error, 'Could not save the role.'));
        }
    };

    const handleDelete = async (roleId: number) => {
        if (window.confirm('Are you sure you want to delete this role? People who hold it lose its permissions.')) {
            try {
                await deleteRole.mutateAsync(roleId.toString());
                queryClient.invalidateQueries({ queryKey: ['roles'] });
                setSnackbar({ message: 'Role deleted.', severity: 'success' });
            } catch (error) {
                setSnackbar({ message: errorMessage(error, 'Could not delete the role.'), severity: 'error' });
            }
        }
    };

    const saving = createRole.isPending || updateRole.isPending;

    if (isLoadingCapabilities) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <AdminPageHeader
                title="Roles & permissions"
                description="A role is a named set of permissions. Give people roles rather than permissions one by one."
                audience="admins"
                action={
                    <Button variant="contained" color="primary" onClick={() => handleOpen()}>
                        Create role
                    </Button>
                }
            />

            <Alert severity="info" sx={{ mb: 2 }}>
                "Create organisations" appears here only when client organisations are included in your plan.
            </Alert>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {initialRoles.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No roles yet. Create one to start giving people permissions.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            initialRoles.map((role: Role) => (
                                <TableRow key={role.id}>
                                    <TableCell>{role.name}</TableCell>
                                    <TableCell>
                                        {role.capabilities.map((c: Capability) => c.name).join(', ')}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton aria-label="Edit role" onClick={() => handleOpen(role)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            aria-label="Delete role"
                                            onClick={() => handleDelete(role.id)}
                                            disabled={deleteRole.isPending}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingRole ? 'Edit role' : 'Create role'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {formError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {formError}
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Name"
                            type="text"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="permissions-label">Permissions</InputLabel>
                            <Select
                                labelId="permissions-label"
                                multiple
                                value={formData.capability_ids}
                                onChange={(e) => setFormData({ ...formData, capability_ids: e.target.value as number[] })}
                                input={<OutlinedInput label="Permissions" />}
                                renderValue={(selected) => {
                                    const selectedCapabilities = capabilities.filter(cap => selected.includes(cap.id));
                                    return selectedCapabilities.map(cap => cap.name).join(', ');
                                }}
                            >
                                {capabilities.map((capability: Capability) => (
                                    <MenuItem key={capability.id} value={capability.id}>
                                        <Checkbox checked={formData.capability_ids.includes(capability.id)} />
                                        <ListItemText primary={capability.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                        <Button type="submit" variant="contained" color="primary" disabled={saving}>
                            {saving ? 'Saving…' : editingRole ? 'Save' : 'Create role'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar?.severity ?? 'success'}
                    onClose={() => setSnackbar(null)}
                    variant="filled"
                >
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
